import React, { useEffect, useState } from "react";
import { Typography } from "@material-tailwind/react";
import api from "../utils/axiosConfig";
import { Hearing } from "../utils/entities/hearing";
import HearingCard from "./HearingCard";
import {
  HearingAgentStatus,
  HearingStatus,
} from "../utils/enums/hearing.enums";

const PendingHearings: React.FC = () => {
  const [invites, setInvitations] = useState<Partial<Hearing>[]>([]);
  const [pendingReports, setPendingReports] = useState<Partial<Hearing>[]>([]);
  const [upcomingHearings, setUpcomingHearings] = useState<Partial<Hearing>[]>(
    []
  );

  useEffect(() => {
    const fetchHearings = async () => {
      try {
        const response = await api.get(`hearings/agent/list`);
        const currentDate = new Date();
        
        // Seus convites de audiências - Status "Convidado"
        setInvitations(
          response.data.filter(
            (hearing: Hearing) =>
              hearing.agentStatus === HearingAgentStatus.Convidado
          )
        );

        // Pendências - Audiências aceitas com data passada (precisam de relatório)
        setPendingReports(
          response.data.filter(
            (hearing: Hearing) =>
              hearing.agentStatus === HearingAgentStatus.Aceito &&
              new Date(hearing.hearingDate!) < currentDate &&
              [
                HearingStatus.Agendada,
                HearingStatus.Confirmada,
                HearingStatus.Realizada,
              ].includes(hearing.status)
          )
        );

        // Suas próximas audiências - Audiências aceitas com data futura
        setUpcomingHearings(
          response.data.filter(
            (hearing: Hearing) =>
              hearing.agentStatus === HearingAgentStatus.Aceito &&
              new Date(hearing.hearingDate!) >= currentDate &&
              [HearingStatus.Agendada, HearingStatus.Confirmada].includes(
                hearing.status
              )
          )
        );
      } catch (error) {
        console.error("Error fetching hearings:", error);
      }
    };

    fetchHearings();
  }, []);

  const removeInviteFromPending = (hearingId: number) => {
    setInvitations((prev) =>
      prev.filter((hearing) => hearing.id !== hearingId)
    );
  };

  if (upcomingHearings.length === 0 && invites.length === 0 && pendingReports.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] p-8 md:p-10 text-center">
        <div className="flex items-center gap-2 text-preppoBlue">
          <h2 className="text-xl md:text-2xl font-semibold text-gray-900">Nenhuma audiência pendente</h2>
        </div>
        <p className="text-gray-500 mt-2 md:mt-3 text-sm md:text-base max-w-md">
          Você não tem audiências programadas no momento.
        </p>
        <button
          type="button"
          className="mt-5 px-4 py-2 rounded-lg border border-gray-200 text-preppoBlue hover:bg-blue-50 transition"
        >
          Explorar Audiências
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-2 md:p-3 lg:p-4 space-y-3 md:space-y-4">
      {/* Seção 1: Seus convites de audiências */}
      {invites.length > 0 && (
        <div className="rounded-xl border border-slate-100 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-2.5">
            <span aria-hidden className="h-2 w-2 rounded-full shrink-0 bg-amber-400"></span>
            <Typography
              variant="h6"
              className="text-slate-600 tracking-tight text-sm md:text-base font-semibold"
            >
              Seus convites
              <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200 align-middle font-medium">Ação necessária</span>
            </Typography>
          </div>
          <div className="p-3 md:p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 md:gap-2.5 items-stretch transition-opacity duration-200">
              {invites.map((hearing) => (
                <div className="h-full" key={hearing.id}>
                  <HearingCard
                    hearing={hearing}
                    removeInviteFromPending={removeInviteFromPending}
                    borderColor="border-l-amber-500"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Seção 2: Pendências - Audiências com data passada que precisam de relatório */}
      {pendingReports.length > 0 && (
        <div className="rounded-xl border border-slate-100 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-2.5">
            <span aria-hidden className="h-2 w-2 rounded-full shrink-0 bg-red-400"></span>
            <Typography
              variant="h6"
              className="text-slate-600 tracking-tight text-sm md:text-base font-semibold"
            >
              Pendências
              <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 ring-1 ring-red-200 align-middle font-medium">Relatório pendente</span>
            </Typography>
          </div>
          <div className="p-3 md:p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 md:gap-2.5 items-stretch transition-opacity duration-200">
              {pendingReports.map((hearing) => (
                <div className="h-full" key={hearing.id}>
                  <HearingCard hearing={hearing} borderColor="border-l-red-500" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Seção 3: Suas próximas audiências */}
      {upcomingHearings.length > 0 && (
        <div className="rounded-xl border border-slate-100 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-2.5">
            <span aria-hidden className="h-2 w-2 rounded-full shrink-0 bg-emerald-400"></span>
            <Typography
              variant="h6"
              className="text-slate-600 tracking-tight text-sm md:text-base font-semibold"
            >
              Suas próximas audiências, diligências ou reuniões
            </Typography>
          </div>
          <div className="p-3 md:p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 md:gap-2.5 items-stretch transition-opacity duration-200">
              {upcomingHearings.map((hearing) => (
                <div className="h-full" key={hearing.id}>
                  <HearingCard hearing={hearing} borderColor="border-l-green-500" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingHearings;
